import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  RefreshCcw, 
  HelpCircle, 
  Brain, 
  CheckCircle,
  Loader2
} from 'lucide-react';
import './RecordingQuestionCard.css';

const RecordingQuestionCard = ({ 
  question, 
  onRefresh, 
  onToggleAnalysisMode, 
  isAnalysisModeActive,
  loading = false,
  onGenerate,
  isEmpty = false
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Show loading state
  if (loading && !question) {
    return (
      <div className="recordingQuestionCard recordingQuestionCard--loading">
        <div className="recordingQuestionCard__content">
          <div className="recordingQuestionCard__header">
            <div className="recordingQuestionCard__titleGroup">
              <Brain size={24} className="recordingQuestionCard__loadingIcon" />
              <h3 className="recordingQuestionCard__title">
                {t('recordingQuestion.title')}
              </h3>
            </div>
          </div>
          <div className="recordingQuestionCard__loadingSkeleton">
            <div className="recordingQuestionCard__skeletonLine recordingQuestionCard__skeletonLine--wide"></div>
            <div className="recordingQuestionCard__skeletonLine recordingQuestionCard__skeletonLine--narrow"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state (no question, not loading)
  if (isEmpty && !question && !loading) {
    return (
      <div className="recordingQuestionCard recordingQuestionCard--empty">
        <div className="recordingQuestionCard__content">
          <div className="recordingQuestionCard__header">
            <div className="recordingQuestionCard__titleGroup">
              <Brain size={24} className="text-gray-400" />
              <h3 className="recordingQuestionCard__title">
                {t('recordingQuestion.title')}
              </h3>
            </div>
          </div>
          <div className="recordingQuestionCard__emptyState">
            <p className="recordingQuestionCard__emptyText">
              {t('recordingQuestion.emptyState', 'No practice question yet. Generate one to get started!')}
            </p>
            {onGenerate && (
              <button 
                className="btn btn--primary recordingQuestionCard__generateBtn"
                onClick={onGenerate}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>{t('recordingQuestion.generating', 'Generating...')}</span>
                  </>
                ) : (
                  <>
                    <Brain size={16} />
                    <span>{t('recordingQuestion.generate', 'Generate Practice Question')}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // If no question and not empty state, don't render
  if (!question) return null;

  const MAX_REPLACEMENTS = 2;
  const replacementsUsed = typeof question.replacements_count === 'number'
    ? question.replacements_count
    : 0;
  const canReplace = replacementsUsed < MAX_REPLACEMENTS;

  const getDifficultyClass = (diff) => {
    switch(diff) {
      case 'easy': return 'recordingQuestionCard__chip--easy';
      case 'moderate': return 'recordingQuestionCard__chip--moderate';
      case 'challenging': return 'recordingQuestionCard__chip--challenging';
      default: return '';
    }
  };

  return (
    <div className={`recordingQuestionCard ${isAnalysisModeActive ? 'recordingQuestionCard--active' : ''}`}>
      {isAnalysisModeActive && (
        <div className="recordingQuestionCard__activeBadge">
          <CheckCircle size={14} />
          <span>{t('recordingQuestion.analysisMode.question')}</span>
        </div>
      )}
      
      <div className="recordingQuestionCard__content">
        <div className="recordingQuestionCard__header">
          <div className="recordingQuestionCard__titleGroup">
            <Brain size={24} className={isAnalysisModeActive ? 'text-blue-500' : 'text-gray-400'} />
            <h3 className="recordingQuestionCard__title">
              {t('recordingQuestion.title')}
            </h3>
          </div>
          <button 
            className="recordingQuestionCard__refreshBtn"
            onClick={onRefresh}
            disabled={loading || !canReplace}
            title={canReplace 
              ? t('recordingQuestion.refreshLimited', { remaining: MAX_REPLACEMENTS - replacementsUsed })
              : t('recordingQuestion.refreshDisabled')
            }
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
            <span className="recordingQuestionCard__refreshText">
              {t('recordingQuestion.refreshShort', 'לתרגול אחר')}
            </span>
            {canReplace && (
              <span className="recordingQuestionCard__refreshCount">
                ({MAX_REPLACEMENTS - replacementsUsed})
              </span>
            )}
          </button>
        </div>

        <p className="recordingQuestionCard__text">
          "{question.text}"
        </p>

        <div className="recordingQuestionCard__chips">
          <span className={`recordingQuestionCard__chip ${getDifficultyClass(question.difficulty)}`}>
            {t(`recordingQuestion.difficulty.${question.difficulty}`)}
          </span>
          <span className="recordingQuestionCard__chip">
            {t('recordingQuestion.roleLabel')}: {question.role || question.focus}
          </span>
        </div>

        {question.context && (
          <div className="recordingQuestionCard__context">
             <button 
              className="recordingQuestionCard__contextToggle"
              onClick={() => setExpanded(!expanded)}
            >
              <HelpCircle size={14} />
              {t('recordingQuestion.contextLabel')}
            </button>
            {expanded && (
              <div className="recordingQuestionCard__contextContent">
                {question.context}
              </div>
            )}
          </div>
        )}

        <div className="recordingQuestionCard__footer">
          <label className="recordingQuestionCard__toggle">
            <div className={`recordingQuestionCard__switch ${isAnalysisModeActive ? 'checked' : ''}`}>
              <div className="recordingQuestionCard__slider" />
            </div>
            <input 
              type="checkbox" 
              checked={isAnalysisModeActive}
              onChange={(e) => onToggleAnalysisMode(e.target.checked)}
              style={{ display: 'none' }}
            />
            <div className="recordingQuestionCard__toggleLabel">
              <span className="recordingQuestionCard__toggleTitle">
                {t('recordingQuestion.toggleLabel')}
              </span>
              <span className="recordingQuestionCard__toggleDesc">
                {t('recordingQuestion.toggleDescription')}
              </span>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default RecordingQuestionCard;
